import {
  ChamberMemberProInvoicePaymentTermsEnum,
  CmpEventCustomFieldAnswerInput,
  CmpEventStatusEnum,
} from "../../../../graphql/types";
import {
  getChamberLocalDateInputValue,
  getInvoiceDueDateInput,
  chamberDateInputToUtcMilli,
} from "../../Invoicing/invoiceDueDate";
import { DateTime } from "luxon";
import { normalizeCmpTimeZone } from "../../../../utils/cmpTimeZones";
import { formatCurrencyFromCents } from "../../../common/dashboard/formatters";
import { isValidEventSalesEmail } from "./eventSalesBilling";
import { EMAIL_FORMAT_MESSAGE } from "../../../../utils/fieldMessages";
import { buildEventAttendeeCustomFieldAnswerInputs } from "./eventSalesAttendeeUpdates";

export const eventInvoiceReferenceMaxLength = 100;

export type EventInvoiceAttendee = {
  companyName?: string;
  customFieldAnswers: Record<string, string>;
  /**
   * Claude Design (8): "provide the details later" fills the name with
   * "Attendee N" (the backend needs a name for every seat). The typed name
   * is kept so unticking restores it.
   */
  detailsLater?: boolean;
  email: string;
  id: string;
  name: string;
  nameBeforeDetailsLater?: string;
  phone: string;
  /** Additional Details (staff notes): staff only, never shown to members. */
  staffNotes?: string;
};

export const eventAttendeeStaffNotesMaxLength = 500;

// CMP Dashboard (21) Q_DEFAULT_TEXT (:11484-11491), IF11 EV8 / Q14: every
// event shows the design dietary title; saved events keep their stored label.
export const eventAttendeeDietaryTitle =
  "Select if you require one of the available dietary options.";
export const getEventAttendeeDietaryCopy = (mealChoice: boolean) =>
  mealChoice
    ? {
        title: "Please select your meal option.",
        placeholder: "Select a meal option",
        search: "Search meal option",
      }
    : {
        title: eventAttendeeDietaryTitle,
        placeholder: "Select a dietary option",
        search: "Search dietary option",
      };
export const eventAttendeeStaffNotesHelper =
  "Provide any additional details pertaining to this attendee and event. These details will be shown in the attendee report and can be updated in Manage Tickets under Update Attendee Details. Not visible to Members.";

export type EventInvoiceAttendeeRequiredField = "email" | "name";

export const getEventInvoiceAttendeeFieldErrorKey = ({
  attendeeId,
  errorKeyPrefix,
  field,
}: {
  attendeeId: string;
  errorKeyPrefix: string;
  field: EventInvoiceAttendeeRequiredField;
}) => `${errorKeyPrefix}.${attendeeId}.${field}`;

/**
 * Attendee 1 is the first attendee of the whole invoice (the first attendee
 * of the first ticket line, or the first included sponsorship attendee).
 * Only that attendee needs an email (Moeen, Sept 24 2026); the backend
 * enforces the same rule on create.
 */
export const getEventInvoicePrimaryAttendeeId = (
  attendeeLists: ReadonlyArray<readonly EventInvoiceAttendee[]>,
): string | undefined => attendeeLists.flat()[0]?.id;

const isEventInvoiceAttendeeEmailValid = ({
  attendee,
  emailRequired,
}: {
  attendee: EventInvoiceAttendee;
  emailRequired: boolean;
}) =>
  attendee.email.trim()
    ? isValidEventSalesEmail(attendee.email)
    : !emailRequired;

/**
 * Every attendee needs a name. Only the attendee with
 * `emailRequiredAttendeeId` needs an email; any other typed email must still
 * be valid.
 */
export const validateEventInvoiceAttendeeFields = ({
  attendees,
  emailRequiredAttendeeId,
  errorKeyPrefix,
}: {
  attendees: readonly EventInvoiceAttendee[];
  emailRequiredAttendeeId: string | undefined;
  errorKeyPrefix: string;
}): Record<string, string> => {
  const errors: Record<string, string> = {};

  attendees.forEach((attendee) => {
    if (!attendee.name.trim()) {
      errors[
        getEventInvoiceAttendeeFieldErrorKey({
          attendeeId: attendee.id,
          errorKeyPrefix,
          field: "name",
        })
      ] = "Attendee Name is required.";
    }

    const emailRequired = attendee.id === emailRequiredAttendeeId;
    const emailError = isEventInvoiceAttendeeEmailValid({ attendee, emailRequired })
      ? null
      : attendee.email.trim()
        ? EMAIL_FORMAT_MESSAGE
        : "Attendee Email is required.";

    if (emailError) {
      errors[
        getEventInvoiceAttendeeFieldErrorKey({
          attendeeId: attendee.id,
          errorKeyPrefix,
          field: "email",
        })
      ] = emailError;
    }
  });

  return errors;
};

let eventInvoiceAttendeeId = 0;

export const createEventInvoiceAttendee = (): EventInvoiceAttendee => {
  eventInvoiceAttendeeId += 1;

  return {
    customFieldAnswers: {},
    email: "",
    id: `event-invoice-attendee-${eventInvoiceAttendeeId}`,
    name: "",
    phone: "",
  };
};

type GetEventInvoiceAttendeeCustomFieldAnswerInputsInput = Readonly<{
  attendee: EventInvoiceAttendee;
}>;

export const getEventInvoiceAttendeeCustomFieldAnswerInputs = ({
  attendee,
}: GetEventInvoiceAttendeeCustomFieldAnswerInputsInput): CmpEventCustomFieldAnswerInput[] =>
  buildEventAttendeeCustomFieldAnswerInputs({
    existingAnswers: [],
    editedAnswers: attendee.customFieldAnswers,
  });

// Design (13) Create Ticket Invoice: each line's discount is a percentage or
// a dollar amount off the whole line (DISCOUNT (% / $) toggle, % first).
export type EventTicketDiscountMode = "%" | "$";

export type EventTicketInvoiceLine = Readonly<{
  attendees: EventInvoiceAttendee[];
  /** Dollars off the whole line, used when discountMode is "$". */
  discountAmount: string;
  discountMode: EventTicketDiscountMode;
  /** Percent off, used when discountMode is "%". */
  discountPercentage: string;
  id: string;
  quantity: number;
  ticketTypeId: string;
}>;

let ticketInvoiceLineId = 0;

export const createEventTicketInvoiceLine = (): EventTicketInvoiceLine => {
  ticketInvoiceLineId += 1;

  return {
    attendees: [],
    discountAmount: "",
    discountMode: "%",
    discountPercentage: "",
    id: `ticket-invoice-line-${ticketInvoiceLineId}`,
    quantity: 0,
    ticketTypeId: "",
  };
};

// A new invoice starts on today's date in the chamber time zone (Moeen,
// Sept 24 2026). The due date follows the chamber Invoice Payment Terms, as
// Create Invoice does. Saved documents retain their persisted dates.
export const getDefaultEventInvoiceDates = ({
  invoicePaymentTerms,
  nowUtcMilli,
  timeZone,
}: {
  invoicePaymentTerms?: ChamberMemberProInvoicePaymentTermsEnum | null;
  nowUtcMilli?: number;
  timeZone?: string | null;
}) => {
  const invoiceDate = getChamberLocalDateInputValue({ nowUtcMilli, timeZone });

  return {
    dueDate: invoiceDate
      ? getEventInvoiceDueDateForIssueDate({
          invoiceDate,
          invoicePaymentTerms,
          timeZone,
        })
      : "",
    invoiceDate,
  };
};

/**
 * Mirrors the backend getCmpEventRegistrationCloseAtUtcMilli: event sales close
 * at 11:59 pm on the event's last local day in the event time zone, never
 * before the event end time.
 */
export const getEventRegistrationCloseAtUtcMilli = ({
  endAtUtcMilli,
  timeZone,
}: {
  endAtUtcMilli: number;
  timeZone?: string | null;
}): number => {
  const endOfLocalDay = DateTime.fromMillis(endAtUtcMilli, {
    zone: normalizeCmpTimeZone(timeZone).timeZone,
  }).endOf("day");
  return endOfLocalDay.isValid
    ? Math.max(endAtUtcMilli, endOfLocalDay.toMillis())
    : endAtUtcMilli;
};

export const getStaffTicketEventEligibility = (event: {
  status: CmpEventStatusEnum;
  startAtUtcMilli: number;
  endAtUtcMilli: number;
  timeZone?: string | null;
} | null | undefined, now = Date.now()) => {
  const valid = Boolean(event && Number.isFinite(event.startAtUtcMilli) &&
    Number.isFinite(event.endAtUtcMilli) && event.endAtUtcMilli >= event.startAtUtcMilli);
  const closed = Boolean(valid && event &&
    (event.status === CmpEventStatusEnum.Completed ||
      getEventRegistrationCloseAtUtcMilli(event) < now));
  return {
    closed,
    nonMemberAllowed: Boolean(valid && event && !closed &&
      [CmpEventStatusEnum.Published, CmpEventStatusEnum.Live].includes(event.status)),
  };
};

/**
 * Why a staff ticket for a non-member is blocked (Karen Novak, Ajax, Oct 19
 * 2026). The rule is the same as getStaffTicketEventEligibility and the
 * cap-backend refusal order: cancelled, then closed, then not published.
 * Text only; it does not change which events allow non-member tickets.
 */
export type StaffTicketNonMemberBlockCause =
  | "invalidDates"
  | "cancelled"
  | "closed"
  | "notPublished";

/** cap-backend `extensions.cmpErrorReason` values for staff ticket refusals. */
export const CMP_STAFF_TICKET_REFUSAL_REASONS = {
  nonMemberEventClosed: "CMP_STAFF_TICKET_NON_MEMBER_EVENT_CLOSED",
  nonMemberEventNotPublished: "CMP_STAFF_TICKET_NON_MEMBER_EVENT_NOT_PUBLISHED",
  nonMemberEventCancelled: "CMP_STAFF_TICKET_NON_MEMBER_EVENT_CANCELLED",
  closedEventConfirmRequired: "CMP_STAFF_TICKET_CLOSED_EVENT_CONFIRM_REQUIRED",
} as const;

export const getStaffTicketNonMemberBlockCause = (event: {
  status: CmpEventStatusEnum;
  startAtUtcMilli: number;
  endAtUtcMilli: number;
  timeZone?: string | null;
} | null | undefined, now = Date.now()): StaffTicketNonMemberBlockCause | null => {
  if (!event) return null;
  const eligibility = getStaffTicketEventEligibility(event, now);
  if (eligibility.nonMemberAllowed) return null;
  const validDates = Number.isFinite(event.startAtUtcMilli) &&
    Number.isFinite(event.endAtUtcMilli) && event.endAtUtcMilli >= event.startAtUtcMilli;
  if (!validDates) return "invalidDates";
  if (event.status === CmpEventStatusEnum.Canceled) return "cancelled";
  if (eligibility.closed) return "closed";
  return "notPublished";
};

export const getStaffTicketNonMemberBlockCauseFromReason = (
  reason: string | undefined,
): StaffTicketNonMemberBlockCause | null => {
  switch (reason) {
    case CMP_STAFF_TICKET_REFUSAL_REASONS.nonMemberEventCancelled:
      return "cancelled";
    case CMP_STAFF_TICKET_REFUSAL_REASONS.nonMemberEventClosed:
      return "closed";
    case CMP_STAFF_TICKET_REFUSAL_REASONS.nonMemberEventNotPublished:
      return "notPublished";
    default:
      return null;
  }
};

/** One plain text per cause. The same words as the cap-backend refusals. */
export const getStaffTicketNonMemberBlockedMessage = ({
  cause,
  eventName,
}: {
  cause: StaffTicketNonMemberBlockCause;
  eventName?: string | null;
}): string => {
  const name = (eventName || "").trim();
  const subject = name || "This event";
  switch (cause) {
    case "cancelled":
      return `${subject} was cancelled, so you cannot sell tickets for it.`;
    case "closed":
      return `Ticket sales for ${name || "this event"} are closed, so you cannot sell a ticket to a non-member. If this guest is with a member business, select Member under Sold To and choose that member.`;
    case "notPublished":
      return `${subject} is not published yet, so you cannot sell a ticket to a non-member. A draft event: go to Events & Sponsorships > Manage Events and select Actions > Publish Event. A scheduled event publishes by itself on its scheduled date.`;
    case "invalidDates":
    default:
      return `The dates for ${name || "this event"} could not be read. Refresh the page and select the event again. If this keeps happening, check the event dates in Events & Sponsorships > Manage Events.`;
  }
};

// Unknown terms use the settings default, Due Upon Receipt.
export const getEventInvoiceDueDateForIssueDate = ({
  invoiceDate,
  invoicePaymentTerms,
  timeZone,
}: {
  invoiceDate: string;
  invoicePaymentTerms?: ChamberMemberProInvoicePaymentTermsEnum | null;
  timeZone?: string | null;
}) =>
  getInvoiceDueDateInput({
    invoicePaymentTerms:
      invoicePaymentTerms || ChamberMemberProInvoicePaymentTermsEnum.DueUponReceipt,
    issueDate: invoiceDate,
    timeZone,
  });

export const isEventInvoiceDateValid = (dateInput: string) =>
  chamberDateInputToUtcMilli({ dateInput, timeZone: "UTC" }) !== undefined;

export const isEventInvoiceDateRangeValid = ({
  dueDate,
  invoiceDate,
}: {
  dueDate: string;
  invoiceDate: string;
}) => isEventInvoiceDateValid(invoiceDate) && isEventInvoiceDateValid(dueDate) && dueDate >= invoiceDate;

export const normalizeEventInvoiceReference = (reference: string) =>
  reference.trim().slice(0, eventInvoiceReferenceMaxLength);

export const parseEventTicketDiscountPercentage = (
  value: string,
): number | null => {
  const normalized = value.trim().replace(/%/g, "");

  // An empty field shows the "0.0" placeholder and means no discount.
  if (!normalized) {
    return 0;
  }
  if (!/^\d{1,3}(?:\.\d)?$/.test(normalized)) {
    return null;
  }

  const percentage = Number(normalized);

  return Number.isFinite(percentage) && percentage >= 0 && percentage <= 100
    ? percentage
    : null;
};

export const formatEventTicketDiscountPercentage = (value: string) => {
  const percentage = parseEventTicketDiscountPercentage(value);

  // Design: a zero discount shows the empty field with its placeholder.
  if (percentage === 0) return "";
  return percentage == null ? value : percentage.toFixed(1);
};

/** Dollars off a line, in cents. Empty is 0; null when the text is not money. */
export const parseEventTicketDiscountAmountInCents = (
  value: string,
): number | null => {
  const normalized = value.trim().replace(/[$,]/g, "");

  if (!normalized) {
    return 0;
  }
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) {
    return null;
  }

  return Math.round(Number(normalized) * 100);
};

export const formatEventTicketDiscountAmount = (value: string) => {
  const amountInCents = parseEventTicketDiscountAmountInCents(value);

  if (amountInCents === 0) return "";
  return amountInCents == null ? value : (amountInCents / 100).toFixed(2);
};

/**
 * Typing rule from the design: digits and one dot with at most two decimals;
 * a percentage stops at 100 and a dollar amount at the line total.
 */
export const limitEventTicketDiscountInput = ({
  grossInCents,
  mode,
  value,
}: {
  grossInCents: number;
  mode: EventTicketDiscountMode;
  value: string;
}): string => {
  let clean = value.replace(/[^0-9.]/g, "");
  const dot = clean.indexOf(".");
  if (dot >= 0) {
    clean = clean.slice(0, dot + 1) + clean.slice(dot + 1).replace(/\./g, "").slice(0, mode === "%" ? 1 : 2);
  }
  if (mode === "%" && Number(clean) > 100) return "100";
  if (mode === "$" && grossInCents > 0 && Math.round(Number(clean) * 100) > grossInCents) {
    return (grossInCents / 100).toFixed(2);
  }
  return clean;
};

export type EventTicketLinePricing = {
  /** Set only in "$" mode; the backend takes the amount off the line total. */
  discountAmountInCents: number | null;
  discountPercentage: number;
  subtotalInCents: number;
};

export const getEventTicketLinePricing = ({
  catalogUnitPriceInCents,
  line,
}: {
  catalogUnitPriceInCents: number;
  line: Pick<EventTicketInvoiceLine, "discountAmount" | "discountMode" | "discountPercentage" | "quantity">;
}): EventTicketLinePricing => {
  const grossInCents = catalogUnitPriceInCents * line.quantity;
  if (line.discountMode === "$") {
    const discountAmountInCents = Math.min(
      parseEventTicketDiscountAmountInCents(line.discountAmount) || 0,
      grossInCents,
    );
    return {
      discountAmountInCents,
      discountPercentage: 0,
      subtotalInCents: grossInCents - discountAmountInCents,
    };
  }
  const discountPercentage =
    parseEventTicketDiscountPercentage(line.discountPercentage) || 0;
  return {
    discountAmountInCents: null,
    discountPercentage,
    subtotalInCents:
      getDiscountedEventTicketUnitPriceInCents({ catalogUnitPriceInCents, discountPercentage }) *
      line.quantity,
  };
};

export const getDiscountedEventTicketUnitPriceInCents = ({
  catalogUnitPriceInCents,
  discountPercentage,
}: {
  catalogUnitPriceInCents: number;
  discountPercentage: number;
}) => {
  const discountTenths = Math.round(discountPercentage * 10);

  return Math.round((catalogUnitPriceInCents * (1000 - discountTenths)) / 1000);
};

export type EventTicketInvoiceDocumentLine = {
  code?: string | null;
  description: string;
  id: string;
  quantity: number;
  totalInCents: number;
  unitPriceInCents: number;
};

/**
 * Line text of a staff sponsorship invoice, as the backend saves it:
 * "{event name} - {package}". Before a package is chosen the preview keeps
 * the placeholder.
 */
export const getEventSponsorshipInvoiceLineDescription = ({
  eventDisplayName,
  packageName,
}: {
  eventDisplayName: string;
  /** null while no sponsorship level is selected. */
  packageName: string | null;
}): string =>
  packageName === null
    ? "Select Sponsorship Level"
    : `${eventDisplayName} - ${packageName.trim() || "Sponsorship"}`;

// Design (11) Create Ticket Invoice: a discounted ticket shows as the
// full-price line plus a separate negative "N% Discount Applied" line, the
// same lines the backend saves on the created invoice.
export const getEventTicketInvoiceDocumentLines = (
  lines: ReadonlyArray<{
    catalogUnitPriceInCents: number;
    code?: string | null;
    discountAmountInCents?: number | null;
    discountPercentage: number;
    id: string;
    name: string;
    quantity: number;
  }>,
  // Issued ticket lines read "{event name} - {ticket}"; discount lines keep
  // the ticket name only.
  eventDisplayName?: string,
): EventTicketInvoiceDocumentLine[] =>
  lines.flatMap((line) => {
    const ticketDescription = eventDisplayName
      ? `${eventDisplayName} - ${line.name}`
      : line.name;
    if (typeof line.discountAmountInCents === "number") {
      const fullPriceLine = {
        code: line.code,
        description: ticketDescription,
        id: line.id,
        quantity: line.quantity,
        totalInCents: line.catalogUnitPriceInCents * line.quantity,
        unitPriceInCents: line.catalogUnitPriceInCents,
      };
      return line.discountAmountInCents > 0
        ? [
            fullPriceLine,
            {
              code: "DISC",
              description: `${formatCurrencyFromCents(line.discountAmountInCents)} Discount Applied \u2013 ${line.name}`,
              id: `${line.id}-discount`,
              quantity: 1,
              totalInCents: -line.discountAmountInCents,
              unitPriceInCents: -line.discountAmountInCents,
            },
          ]
        : [fullPriceLine];
    }
    const discountedUnitPriceInCents = getDiscountedEventTicketUnitPriceInCents({
      catalogUnitPriceInCents: line.catalogUnitPriceInCents,
      discountPercentage: line.discountPercentage,
    });
    const discountInCents =
      (line.catalogUnitPriceInCents - discountedUnitPriceInCents) * line.quantity;
    const ticketLine = {
      code: line.code,
      description: ticketDescription,
      id: line.id,
      quantity: line.quantity,
      totalInCents: line.catalogUnitPriceInCents * line.quantity,
      unitPriceInCents: line.catalogUnitPriceInCents,
    };
    if (line.discountPercentage <= 0 || discountInCents <= 0) {
      return [ticketLine];
    }
    const percentLabel = String(Math.round(line.discountPercentage * 10) / 10);
    return [
      ticketLine,
      {
        code: "DISC",
        description: `${percentLabel}% Discount Applied \u2013 ${line.name}`,
        id: `${line.id}-discount`,
        quantity: 1,
        totalInCents: -discountInCents,
        unitPriceInCents: -discountInCents,
      },
    ];
  });

/**
 * Design taxRuleFor("ticket") exemptItems: ticket types marked No Tax in
 * Create Event are left out of tax. A discount on a No Tax line stays on it.
 * The backend applies the same rule to the saved sale.
 */
export const getEventTicketTaxableSubtotalInCents = (
  lines: ReadonlyArray<Readonly<{ noTax?: boolean | null; subtotalInCents: number }>>,
): number =>
  lines.reduce((sum, line) => (line.noTax ? sum : sum + line.subtotalInCents), 0);

/** "Tax not applied to: Speaker Ticket." (design D:1266985). */
export const getEventTicketTaxNotAppliedNote = (
  ticketNames: ReadonlyArray<string>,
): string | null => {
  const names = Array.from(
    new Set(ticketNames.map((name) => name.trim()).filter(Boolean)),
  );
  return names.length ? `Tax not applied to: ${names.join(", ")}.` : null;
};

export const getEventInvoiceTaxAmountInCents = ({
  subtotalInCents,
  taxExempt,
  taxRatePercentage,
}: {
  subtotalInCents: number;
  taxExempt: boolean;
  taxRatePercentage: number;
}) => (taxExempt ? 0 : Math.round((subtotalInCents * taxRatePercentage) / 100));

export const resizeEventTicketLineAttendees = ({
  attendees,
  attendeeCount,
}: {
  attendees: EventInvoiceAttendee[];
  attendeeCount: number;
}): EventInvoiceAttendee[] => {
  const normalizedCount = Math.max(0, Math.floor(attendeeCount));

  if (normalizedCount <= attendees.length) {
    return attendees.slice(0, normalizedCount);
  }

  return [
    ...attendees,
    ...Array.from({ length: normalizedCount - attendees.length }, () =>
      createEventInvoiceAttendee(),
    ),
  ];
};

export const hasDuplicateEventTicketTypes = (
  lines: ReadonlyArray<EventTicketInvoiceLine>,
) => {
  const selectedTicketTypeIds = lines
    .map((line) => line.ticketTypeId)
    .filter(Boolean);

  return new Set(selectedTicketTypeIds).size !== selectedTicketTypeIds.length;
};

export const isEventInvoiceAttendeeComplete = (
  attendee: EventInvoiceAttendee,
  { emailRequired }: { emailRequired: boolean },
) =>
  Boolean(attendee.name.trim()) &&
  isEventInvoiceAttendeeEmailValid({ attendee, emailRequired });
